import { Link } from 'react-router-dom';
import { MassCountdown } from '@/components/events/MassCountdown';
import { Badge } from '@/components/shared/Badge';
import { MassLifecycleBadge } from '@/components/shared/MassLifecycleBadge';
import { Panel } from '@/components/shared/Panel';
import { eventRoutes } from '@/data/routes';
import { getMassLifecycleSummary } from '@/features/events/selectors';
import { useAuth } from '@/hooks/useAuth';
import type { EventListItem } from '@/types/domain';
import { formatDateTime } from '@/utils/date';

interface EventCardProps {
  event: EventListItem;
}

function getPrimaryCtaLabel(
  event: EventListItem,
  canManageEvents: boolean,
  lifecycle = getMassLifecycleSummary(event),
): string {
  if (lifecycle.isFinishedLike) {
    return canManageEvents ? 'Review Results' : 'View Results';
  }

  if (lifecycle.isStarted) {
    return canManageEvents ? 'Manage Live Mass' : 'View Live Mass';
  }

  if (canManageEvents) {
    return 'Manage Mass';
  }

  if (event.currentUserSignupStatus === 'joined') {
    return 'View My Signup';
  }

  if (event.currentUserSignupStatus === 'waitlist') {
    return 'View My Waitlist';
  }

  if (lifecycle.isSignupAvailable) {
    return event.isFull ? 'Mass Full' : 'Sign Up';
  }

  return 'View Mass';
}

export function EventCard({ event }: EventCardProps): JSX.Element {
  const { hasAnyRole } = useAuth();
  const canManageEvents = hasAnyRole(['admin', 'officer', 'mass_manager']);
  const lifecycle = getMassLifecycleSummary(event);
  const primaryCtaLabel = getPrimaryCtaLabel(event, canManageEvents, lifecycle);
  const countdownState = lifecycle.isFinishedLike ? 'finished' : lifecycle.isStarted ? 'started' : 'scheduled';
  const userStatusLabel =
    event.currentUserSignupStatus === 'joined'
      ? lifecycle.isStarted || lifecycle.isFinishedLike
        ? 'Rostered'
        : 'You Joined'
      : event.currentUserSignupStatus === 'waitlist'
        ? lifecycle.isStarted || lifecycle.isFinishedLike
          ? 'Waitlisted'
          : 'You Waitlisted'
        : null;

  return (
    <Panel
      title={event.title}
      description={event.description}
      action={
        <div className="panel-actions">
          <Link to={eventRoutes.detail(event.id)} className="button button--secondary">
            {primaryCtaLabel}
          </Link>
          {canManageEvents ? (
            <Link to={eventRoutes.edit(event.id)} className="button button--ghost">
              Edit Mass
            </Link>
          ) : null}
        </div>
      }
    >
      <div className="event-card__meta">
        <MassLifecycleBadge summary={lifecycle} />
        {event.isFull && lifecycle.isSignupAvailable ? <Badge tone="warning">Full</Badge> : null}
        {userStatusLabel ? <Badge tone={event.currentUserSignupStatus === 'joined' ? 'success' : 'info'}>{userStatusLabel}</Badge> : null}
        <MassCountdown startsAt={event.startsAt} compact state={countdownState} />
      </div>
      <dl className="event-card__stats">
        <div>
          <dt>Local Start</dt>
          <dd>{formatDateTime(event.startsAt)}</dd>
        </div>
        <div>
          <dt>Local Close</dt>
          <dd>{formatDateTime(event.signupClosesAt)}</dd>
        </div>
        <div>
          <dt>Joined</dt>
          <dd>{event.totalJoined}</dd>
        </div>
        <div>
          <dt>Required</dt>
          <dd>{event.totalRequired}</dd>
        </div>
        <div>
          <dt>Waitlist</dt>
          <dd>{event.totalWaitlist}</dd>
        </div>
        <div>
          <dt>Remaining</dt>
          <dd>{event.remainingCount}</dd>
        </div>
      </dl>
    </Panel>
  );
}
